import React, { useState } from 'react'
import { COLORS } from '../theme'
import { breakIndex } from './breaks.js'

// Trend chart for one metric: week / month / quarter, stacked by T-shirt size
// where the metric carries a breakdown, with a rolling average over the top.
//
// Inline SVG, no chart library — the bundle is already ~400kB and this draws
// bars, a line and an axis. The decisions here are about not misleading:
//
//  - Y-AXIS STARTS AT ZERO, with gridlines drawn. A truncated axis turns a 5%
//    wobble into a cliff, which on an exec board is the difference between
//    "steady" and "something broke".
//  - THE ROLLING AVERAGE IS THE POINT. Weekly epic counts are noisy enough that
//    a 2.2x claim was made off them and then retracted. Bars show the noise; the
//    line shows the trend; neither is allowed to stand in for the other.
//  - A GAP STAYS A GAP. An unmeasured week draws no bar and breaks the average
//    line — never a zero-height bar, which reads as a perfect week.
//  - PARTIAL BUCKETS ARE HATCHED. A 13-week window clips its first month and
//    both end quarters; an unmarked short bar reads as a collapse.
//  - A BREAK IS DRAWN, NOT SMOOTHED. Where the source says the same name
//    starts measuring something different (`breaks`, e.g. lead time across the
//    1 July 2026 Kanban cutover), a dashed line marks the first bucket after it
//    and the reason is printed under the chart, so a step there is read as the
//    definition moving rather than the team.
//
// Size ramp runs light→dark so the stack reads as one ordered quantity rather
// than five unrelated categories.
const BAND_ORDER = ['XS', 'S', 'M', 'L', 'XL']
const BAND_COLOR = {
    XS: '#93c5fd',
    S: '#60a5fa',
    M: '#3b82f6',
    L: '#2563eb',
    XL: '#1d4ed8',
}

const VB_W = 720
const VB_H = 300
const PAD = { top: 18, right: 14, bottom: 34, left: 40 }

const fmt = (n) =>
    n === null || n === undefined ? '—' : Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10)

function label(x, mode) {
    const s = String(x)
    if (mode === 'quarter') return s.replace('-', ' ')
    const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    if (mode === 'month') {
        const [y, m] = s.split('-')
        return `${MON[Number(m) - 1]} ${y.slice(2)}`
    }
    const [, m, d] = s.split('-')
    return `${Number(m)}/${Number(d)}`
}

// '2026-10-12' → '12 Oct', the way the tile names the same week.
function dayMonth(x) {
    const [, m, d] = String(x).split('-')
    const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `${Number(d)} ${MON[Number(m) - 1]}`
}

/**
 * Trailing mean over `n` measured points. Gaps break it rather than counting as
 * 0, and so does a break mark: an average reaching back across a definition
 * change would smooth over exactly the step the mark is there to show.
 */
function rollingAverage(points, n = 4, breaksAt = []) {
    return points.map((p, i) => {
        if (p.y === null || p.y === undefined) return null
        const floor = Math.max(0, ...breaksAt.filter((b) => b <= i))
        const window = points.slice(Math.max(floor, i - n + 1), i + 1).filter((q) => q.y !== null && q.y !== undefined)
        if (window.length === 0) return null
        return window.reduce((s, q) => s + q.y, 0) / window.length
    })
}

/**
 * Nice-ish round ticks from 0 to the first tick AT OR ABOVE max.
 *
 * The top tick is the top of the plot, so it has to clear the tallest bar.
 * Stopping at the last tick below max drew every bar above it off the top of
 * the chart with its number clipped — the best weeks, which are the ones a
 * reader looks for first.
 */
export function ticks(max) {
    if (max <= 0) return [0]
    const raw = max / 4
    const mag = Math.pow(10, Math.floor(Math.log10(raw)))
    const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || mag * 10
    const out = [0]
    while (out[out.length - 1] < max) out.push(Math.round((out[out.length - 1] + step) * 100) / 100)
    return out
}

export default function Chart({
    weekly,
    monthly,
    quarterly,
    monthlyAgg,
    unit,
    breaks = null,
    height = 300,
}) {
    const has = (s) => Array.isArray(s) && s.some((p) => p && p.y !== null && p.y !== undefined)
    const available = { week: has(weekly), month: has(monthly), quarter: has(quarterly) }
    const firstMode = ['week', 'month', 'quarter'].find((m) => available[m])

    const [mode, setMode] = useState(firstMode || 'week')
    const [hover, setHover] = useState(null)
    const [isolated, setIsolated] = useState(null)
    const [showAvg, setShowAvg] = useState(true)

    if (!firstMode) return null
    const series = mode === 'week' ? weekly : mode === 'month' ? monthly : quarterly
    const all = (series || []).filter(Boolean)
    const points = all.filter((p) => p.y !== null && p.y !== undefined)
    if (points.length === 0) return null

    // Stack only where the data actually carries a breakdown.
    const stacked = points.some((p) => p.bands)
    const bandsPresent = BAND_ORDER.filter((b) => points.some((p) => p.bands && p.bands[b] > 0))
    const activeBands = isolated ? [isolated] : bandsPresent

    const barValue = (p) =>
        stacked && isolated ? (p.bands ? p.bands[isolated] || 0 : 0) : p.y

    const maxVal = Math.max(...all.map((p) => (p.y === null ? 0 : barValue(p))), 0)
    const tickVals = ticks(maxVal || 1)
    const top = tickVals[tickVals.length - 1] || 1

    const plotW = VB_W - PAD.left - PAD.right
    const plotH = VB_H - PAD.top - PAD.bottom
    const slot = plotW / all.length
    const barW = Math.min(slot * 0.66, 46)
    const yOf = (v) => PAD.top + plotH - (v / top) * plotH
    const xOf = (i) => PAD.left + i * slot + slot / 2
    // A year of weeks is ~50 buckets; every date under every bar ran together
    // into one unreadable strip (the exec view, 2026-10-26). Thin the axis to
    // about 13 dates; the hover still names every bucket.
    const labelEvery = Math.max(1, Math.ceil(all.length / 13))

    const breaksAt = (breaks || []).map((b) => breakIndex(all, b, mode)).filter((i) => i > 0)
    const avg = rollingAverage(all, mode === 'week' ? 4 : 3, breaksAt)
    // The pen lifts at a gap and at a break, so the line visibly restarts there.
    let avgPath = ''
    let pen = false
    avg.forEach((v, i) => {
        if (v === null) {
            pen = false
            return
        }
        avgPath += `${!pen || breaksAt.includes(i) ? 'M' : 'L'}${xOf(i)},${yOf(v)} `
        pen = true
    })

    // Summary strip — the numbers worth reading before the shape.
    const measured = points
    const total = measured.reduce((s, p) => s + p.y, 0)
    const best = measured.reduce((a, b) => (b.y > a.y ? b : a), measured[0])
    const mean = total / measured.length
    const last = measured[measured.length - 1]
    // No "first → last" chip. It compared one bucket to one bucket, so on
    // 2026-10-21 the ELT board showed a green "+1040%" (a 5-point first week
    // against 57) beside the tile's own red "-15.6% vs prior 4 weeks". The tile
    // header carries the change worth reading; the chart shows the shape.
    const gaps = all.length - measured.length

    const hovered = hover !== null ? all[hover] : null
    const marks = (breaks || []).map((b) => ({ ...b, i: breakIndex(all, b, mode) })).filter((b) => b.i > 0)

    const btn = (activeState, onClick, children, enabled = true, key) => (
        <button
            key={key}
            disabled={!enabled}
            onClick={onClick}
            style={{
                background: activeState ? COLORS.primary : 'transparent',
                border: `1px solid ${activeState ? COLORS.primary : COLORS.border}`,
                color: !enabled ? COLORS.border : activeState ? '#fff' : COLORS.textMuted,
                borderRadius: 5,
                padding: '3px 10px',
                fontSize: 11.5,
                cursor: enabled ? 'pointer' : 'not-allowed',
                fontWeight: activeState ? 600 : 400,
            }}
        >
            {children}
        </button>
    )

    const stat = (k, v, title, sub) => (
        <div style={{ minWidth: 74 }} title={title}>
            <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {k}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
                {v}
                {sub && <span style={{ fontSize: 11, fontWeight: 400, color: COLORS.textMuted, marginLeft: 5 }}>{sub}</span>}
            </div>
        </div>
    )
    const bucketWord = mode === 'week' ? 'week' : mode === 'month' ? 'month' : 'quarter'

    return (
        <div style={{ marginTop: 12 }}>
            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {btn(mode === 'week', () => setMode('week'), 'Week', available.week, 'w')}
                {btn(mode === 'month', () => setMode('month'), 'Month', available.month, 'm')}
                {btn(mode === 'quarter', () => setMode('quarter'), 'Quarter', available.quarter, 'q')}
                <div style={{ width: 8 }} />
                {btn(showAvg, () => setShowAvg((v) => !v), `${mode === 'week' ? '4' : '3'}-pt avg`, true, 'a')}
            </div>

            {/* Summary strip */}
            <div
                style={{
                    display: 'flex',
                    gap: 20,
                    padding: '9px 12px',
                    background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 7,
                    marginBottom: 12,
                    flexWrap: 'wrap',
                }}
            >
                {/* A mean per bucket, never a total. "Total 919" summed 21 weeks
                    of size points into a number nobody budgets in (Ken's read,
                    2026-10-25); a weekly mean sits beside the latest week. */}
                {stat(`Mean / ${bucketWord}`, fmt(mean), 'Across the measured buckets shown')}
                {/* "Highest", not "Best": on a bug count the biggest bucket is the
                    worst one, and "Best 12" read as praise for the worst week. */}
                {stat('Highest', `${fmt(best.y)}`, `Highest: ${label(best.x, mode)}`)}
                {/* The bucket is printed, not left to a hover: "Latest 57" did not
                    say it was the week of 12 Oct. */}
                {stat('Latest', fmt(last.y), undefined, mode === 'week' ? `wk of ${dayMonth(last.x)}` : label(last.x, mode))}
                {stat(`${mode === 'week' ? 'Weeks' : mode === 'month' ? 'Months' : 'Quarters'}`, `${measured.length}`, 'Measured in the window')}
                {gaps > 0 &&
                    stat('Gaps', `${gaps}`, 'Buckets with no measurement — drawn as a break, never as zero')}
            </div>

            {/* Chart */}
            <div style={{ position: 'relative' }}>
                <svg
                    viewBox={`0 0 ${VB_W} ${VB_H}`}
                    style={{ width: '100%', height, display: 'block' }}
                    role="img"
                    onMouseLeave={() => setHover(null)}
                >
                    <defs>
                        <pattern id="pHatch" width="5" height="5" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                            <rect width="5" height="5" fill={COLORS.cardBg} />
                            <line x1="0" y1="0" x2="0" y2="5" stroke={COLORS.textMuted} strokeWidth="1.6" />
                        </pattern>
                    </defs>

                    {/* Gridlines + y axis. Zero is drawn brighter so the axis is visibly untruncated. */}
                    {tickVals.map((t) => (
                        <g key={t}>
                            <line
                                x1={PAD.left}
                                y1={yOf(t)}
                                x2={VB_W - PAD.right}
                                y2={yOf(t)}
                                stroke={COLORS.border}
                                strokeWidth={t === 0 ? 1.4 : 0.7}
                                opacity={t === 0 ? 1 : 0.5}
                            />
                            <text x={PAD.left - 7} y={yOf(t) + 3.5} textAnchor="end" style={{ fontSize: 10, fill: COLORS.textMuted }}>
                                {fmt(t)}
                            </text>
                        </g>
                    ))}

                    {all.map((p, i) => {
                        const isGap = p.y === null || p.y === undefined
                        const x = xOf(i) - barW / 2
                        if (isGap) {
                            return (
                                <g key={p.x}>
                                    <rect
                                        x={x}
                                        y={PAD.top}
                                        width={barW}
                                        height={plotH}
                                        fill="transparent"
                                        onMouseEnter={() => setHover(i)}
                                    />
                                    <text
                                        x={xOf(i)}
                                        y={yOf(0) - 5}
                                        textAnchor="middle"
                                        style={{ fontSize: 10, fill: COLORS.textMuted }}
                                    >
                                        no data
                                    </text>
                                </g>
                            )
                        }

                        const v = barValue(p)
                        let cursorY = yOf(0)
                        const segs =
                            stacked && p.bands
                                ? activeBands.map((b) => {
                                      const bv = p.bands[b] || 0
                                      const h = (bv / top) * plotH
                                      cursorY -= h
                                      return { b, bv, y: cursorY, h }
                                  })
                                : [{ b: null, bv: v, y: yOf(v), h: (v / top) * plotH }]

                        return (
                            <g key={p.x} onMouseEnter={() => setHover(i)}>
                                {segs.map((sg) =>
                                    sg.h <= 0 ? null : (
                                        <rect
                                            key={sg.b || 'v'}
                                            x={x}
                                            y={sg.y}
                                            width={barW}
                                            height={sg.h}
                                            fill={p.partial ? 'url(#pHatch)' : sg.b ? BAND_COLOR[sg.b] : COLORS.primary}
                                            opacity={hover === null || hover === i ? 1 : 0.45}
                                        />
                                    )
                                )}
                                {/* THE NUMBER ON THE BAR. */}
                                <text
                                    x={xOf(i)}
                                    y={yOf(v) - 5}
                                    textAnchor="middle"
                                    style={{
                                        fontSize: slot < 20 ? 8.5 : 11,
                                        fontWeight: 700,
                                        fill: hover === null || hover === i ? COLORS.text : COLORS.textMuted,
                                    }}
                                >
                                    {fmt(v)}
                                </text>
                                {i % labelEvery === 0 && (
                                    <text
                                        x={xOf(i)}
                                        y={VB_H - PAD.bottom + 15}
                                        textAnchor="middle"
                                        style={{ fontSize: 10, fill: COLORS.textMuted }}
                                    >
                                        {label(p.x, mode)}
                                    </text>
                                )}
                                {p.partial && (
                                    <text
                                        x={xOf(i)}
                                        y={VB_H - PAD.bottom + 26}
                                        textAnchor="middle"
                                        style={{ fontSize: 8.5, fill: COLORS.warning }}
                                    >
                                        partial
                                    </text>
                                )}
                            </g>
                        )
                    })}

                    {/* Where the line changes meaning: a dashed rule at the left edge
                        of the first bucket after the change. */}
                    {marks.map((b) => (
                        <g key={`brk-${b.x || b.date}`}>
                            <title>{b.label}</title>
                            <line
                                x1={xOf(b.i) - slot / 2}
                                y1={PAD.top - 6}
                                x2={xOf(b.i) - slot / 2}
                                y2={yOf(0)}
                                stroke={COLORS.warning}
                                strokeWidth="1.4"
                                strokeDasharray="4 3"
                            />
                            <text
                                x={xOf(b.i) - slot / 2 + 4}
                                y={PAD.top + 2}
                                style={{ fontSize: 10, fill: COLORS.warning }}
                            >
                                definition changes
                            </text>
                        </g>
                    ))}

                    {/* Rolling average, drawn over the bars. */}
                    {showAvg && !isolated && (
                        <path
                            d={avgPath.trim()}
                            fill="none"
                            stroke={COLORS.warning}
                            strokeWidth="2"
                            strokeLinecap="round"
                            opacity="0.95"
                        />
                    )}
                </svg>

                {/* Tooltip */}
                {hovered && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 4,
                            left: `${Math.min(Math.max((xOf(hover) / VB_W) * 100, 4), 66)}%`,
                            background: COLORS.background,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 7,
                            padding: '8px 11px',
                            pointerEvents: 'none',
                            minWidth: 132,
                            boxShadow: '0 6px 20px rgba(0,0,0,0.45)',
                        }}
                    >
                        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
                            {label(hovered.x, mode)}
                            {hovered.partial && (
                                <span style={{ color: COLORS.warning, fontWeight: 400 }}> · partial</span>
                            )}
                        </div>
                        {hovered.y === null ? (
                            <div style={{ fontSize: 11.5, color: COLORS.textMuted }}>
                                Not measured — drawn as a break, never as a zero.
                            </div>
                        ) : (
                            <>
                                <div style={{ fontSize: 13, color: COLORS.text }}>
                                    {fmt(hovered.y)} {unit || ''}
                                </div>
                                {hovered.bands && (
                                    <div style={{ marginTop: 5 }}>
                                        {BAND_ORDER.filter((b) => hovered.bands[b] > 0).map((b) => (
                                            <div
                                                key={b}
                                                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}
                                            >
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        background: BAND_COLOR[b],
                                                        borderRadius: 2,
                                                    }}
                                                />
                                                <span style={{ color: COLORS.textMuted, width: 20 }}>{b}</span>
                                                <span style={{ color: COLORS.text }}>{hovered.bands[b]}</span>
                                                {/* A weighted series stacks points, not epics; the
                                                    count behind a segment rides along as `counts`. */}
                                                {hovered.counts && (
                                                    <span style={{ color: COLORS.textMuted }}>
                                                        · {hovered.counts[b]} epic{hovered.counts[b] === 1 ? '' : 's'}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {hovered.weeks !== undefined && (
                                    <div style={{ fontSize: 10.5, color: COLORS.textMuted, marginTop: 5 }}>
                                        {hovered.weeks} week{hovered.weeks === 1 ? '' : 's'} of data
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Legend — click a size to isolate it. */}
            {stacked && bandsPresent.length > 0 && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
                    {bandsPresent.map((b) => (
                        <button
                            key={b}
                            onClick={() => setIsolated(isolated === b ? null : b)}
                            title={isolated === b ? 'Show all sizes' : `Show only ${b} epics`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                                background: 'transparent',
                                border: `1px solid ${isolated === b ? BAND_COLOR[b] : 'transparent'}`,
                                borderRadius: 5,
                                padding: '2px 7px',
                                cursor: 'pointer',
                                opacity: isolated && isolated !== b ? 0.4 : 1,
                            }}
                        >
                            <span style={{ width: 9, height: 9, background: BAND_COLOR[b], borderRadius: 2 }} />
                            <span style={{ fontSize: 11.5, color: COLORS.textMuted }}>{b}</span>
                        </button>
                    ))}
                    {isolated && (
                        <span style={{ fontSize: 11, color: COLORS.warning }}>
                            showing {isolated} only — rolling average hidden
                        </span>
                    )}
                    {showAvg && !isolated && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 4 }}>
                            <span style={{ width: 14, height: 2, background: COLORS.warning, borderRadius: 2 }} />
                            <span style={{ fontSize: 11.5, color: COLORS.textMuted }}>
                                {mode === 'week' ? '4' : '3'}-point rolling average
                            </span>
                        </span>
                    )}
                </div>
            )}

            {marks.map((b) => (
                <div
                    key={`brk-note-${b.x || b.date}`}
                    style={{ fontSize: 11, color: COLORS.warning, marginTop: 8, lineHeight: 1.5 }}
                >
                    Dashed line, {label(all[b.i].x, mode)}: {b.label}
                </div>
            ))}

            {mode !== 'week' && (
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                    {monthlyAgg === 'sum'
                        ? `${mode === 'month' ? 'Monthly' : 'Quarterly'} total of the weekly figures.`
                        : 'Mean of the weekly figures — not a recomputed percentile.'}
                    {all.some((p) => p.partial) && ' Hatched bars are partial buckets.'}
                </div>
            )}
        </div>
    )
}
